// REACT IMPORTS
import React, { useEffect, useMemo, useState } from "react";

// MUI COMPONENTS
import {
	IconButton,
	MenuItem,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
} from "@mui/material";

// MUI ICONS
import EditIcon from "@mui/icons-material/Edit";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import UnarchiveOutlinedIcon from "@mui/icons-material/UnarchiveOutlined";

// CUSTOM COMPONENTS
import {
	SortableHeaderCell,
	useSortedRows,
} from "../../Admin/TimeOffConsole/sortableTable";
import HelpLabel, { HELP } from "./HelpLabel";
import TanStackTablePagination from "../../Misc/Table/TanStackTablePagination/TanStackTablePagination";
import usePagedRows from "./usePagedRows";

// CONSTANTS
import { fmtHours } from "../../Projects/ProjectViewer/BudgetEditorTab";
import { fmtDate, fmtMoney, pillStyle, plural } from "./productLibraryUtils";
import { IN_EDITOR } from "./productLibraryUtils";

const ANY = "";
const NONE = "__none__";
const SET_PILL = { fg: "#184c93", bg: "#e8f0fe" };

const editedPct = (p) =>
	p.uses ? Math.round((p.editedCount / p.uses) * 100) : null;

// Sort values per column
const SORT_GETTERS = {
	name: (p) => p.text,
	phase: (p) => p.phaseNameLabel || "",
	services: (p) => p.services.join(", "),
	components: (p) => p.components.join(", "),
	rate: (p) => p.rate,
	typicalHours: (p) => p.typicalHours,
	uses: (p) => p.uses,
	editedPct: (p) => editedPct(p),
	lastUsed: (p) => p.lastUsed,
	modified: (p) => p.modified,
};
// Numbers and dates sort highest / newest first on the first click
const DESC_FIRST = [
	"rate",
	"typicalHours",
	"uses",
	"editedPct",
	"lastUsed",
	"modified",
];

// A filter dropdown that shows its value, with the label above
const FilterSelect = ({ label, value, onChange, width, children }) => (
	<TextField
		select
		size="small"
		label={label}
		value={value}
		onChange={(e) => onChange(e.target.value)}
		SelectProps={{ displayEmpty: true }}
		InputLabelProps={{ shrink: true }}
		sx={{ width }}
	>
		{children}
	</TextField>
);

// Matches a product's tags against a Phase / Service / Component filter
const tagMatch = (tags, filter) =>
	filter === ANY
		? true
		: filter === NONE
			? !tags.length
			: tags.includes(filter);

/**
 * The Product Library's Active / Inactive table: compact rows, a filter bar,
 * and click-to-sort columns
 * @param {Array} props.products - the rows for this tab
 * @param {Object} props.options - { phases, services, components }
 * @param {boolean} props.canEdit
 * @param {string} props.emptyText - shown when the tab has no products
 * @param {Function} props.onUsage - (product)
 * @param {Function} props.onEdit - (product)
 * @param {Function} props.onRetire - (product), retire or reactivate
 */
const ProductTable = ({
	products,
	options,
	canEdit,
	emptyText,
	onUsage,
	onEdit,
	onRetire,
}) => {
	// Screen width, for hiding lower-priority columns on laptops
	const [screenWidth, setScreenWidth] = useState(window.innerWidth);
	useEffect(() => {
		const onResize = () => setScreenWidth(window.innerWidth);
		window.addEventListener("resize", onResize);
		return () => window.removeEventListener("resize", onResize);
	}, []);

	// FILTERS
	const [search, setSearch] = useState("");
	const [phase, setPhase] = useState(ANY);
	const [service, setService] = useState(ANY);
	const [component, setComponent] = useState(ANY);
	const [library, setLibrary] = useState(ANY);

	const libraryOptions = useMemo(
		() => [
			IN_EDITOR,
			...[
				...new Set(products.map((p) => p.hiddenReason).filter(Boolean)),
			].sort(),
		],
		[products],
	);

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		return products.filter((p) => {
			if (
				q &&
				!`${p.text} ${p.description || ""}`.toLowerCase().includes(q)
			)
				return false;
			if (!tagMatch(p.phaseNames, phase)) return false;
			if (!tagMatch(p.services, service)) return false;
			if (!tagMatch(p.components, component)) return false;
			if (library && (p.hiddenReason || IN_EDITOR) !== library)
				return false;
			return true;
		});
	}, [products, search, phase, service, component, library]);

	const { sorted, sortKey, sortDir, onSort } = useSortedRows(
		filtered,
		SORT_GETTERS,
		"modified",
		"desc",
		DESC_FIRST,
	);

	// 25 a page like the site's other tables; back to page 1 when the
	// filters or sort change
	const {
		pageRows,
		table: pager,
		pagination,
	} = usePagedRows(
		sorted,
		[search, phase, service, component, library, sortKey, sortDir].join(
			"|",
		),
	);

	const filtersOn =
		search || phase || service || component || library ? true : false;
	const clearFilters = () => {
		setSearch("");
		setPhase(ANY);
		setService(ANY);
		setComponent(ANY);
		setLibrary(ANY);
	};

	// Columns, in order. "minWidth" hides a column on screens narrower than
	// that, so Name keeps room on laptops; Edited and Last Used are also in
	// the Product Usage dialog, and nearly every product is $200.
	const allColumns = [
		{
			key: "name",
			label: "Name",
			className: "productTableNameCell",
			cell: (p) => (
				<>
					<span className="bulkTagName" title={p.text}>
						{p.text}
					</span>
					{(p.hiddenReason || p.sets.length > 0) && (
						<span className="productTableNotes">
							{p.hiddenReason && (
								<Tooltip
									title={HELP.editor}
									arrow
									placement="top"
								>
									<span className="productLibraryMuted">
										{p.hiddenReason}
									</span>
								</Tooltip>
							)}
							{p.sets.map((s) => (
								<Tooltip
									key={s.id}
									title={HELP.sets}
									arrow
									placement="top"
								>
									<span style={pillStyle(SET_PILL)}>
										{s.name}
									</span>
								</Tooltip>
							))}
						</span>
					)}
				</>
			),
		},
		{
			key: "phase",
			label: "Phase",
			width: 150,
			cell: (p) => (
				<>
					<span>{p.phaseNameLabel}</span>
					{p.description && (
						<span className="productTableSubtext">
							{p.description}
						</span>
					)}
				</>
			),
		},
		{
			key: "services",
			label: "Service",
			width: 130,
			className: "productTableTagCell",
			cell: (p) => p.services.join(", "),
		},
		{
			key: "components",
			label: "Component",
			width: 130,
			className: "productTableTagCell",
			cell: (p) => p.components.join(", "),
		},
		{
			key: "rate",
			label: "Rate",
			width: 70,
			align: "right",
			minWidth: 1440,
			cell: (p) => (p.rate !== null ? fmtMoney(p.rate) : ""),
		},
		{
			key: "typicalHours",
			label: "Typical Hrs",
			width: 150,
			align: "right",
			help: HELP.typicalHours,
			cell: (p) =>
				p.typicalHours !== null ? fmtHours(p.typicalHours) : "",
		},
		{
			key: "uses",
			label: "Uses",
			width: 96,
			align: "right",
			help: HELP.uses,
			cell: (p) => p.uses,
		},
		{
			key: "editedPct",
			label: "Edited",
			width: 104,
			align: "right",
			help: HELP.editedPct,
			minWidth: 1800,
			cell: (p) => {
				const pct = editedPct(p);
				return pct !== null ? `${pct}%` : "";
			},
		},
		{
			key: "lastUsed",
			label: "Last Used",
			width: 128,
			help: HELP.lastUsed,
			minWidth: 1800,
			className: "productLibraryNoWrap",
			cell: (p) => (p.lastUsed ? fmtDate(p.lastUsed) : ""),
		},
		{
			key: "modified",
			label: "Updated",
			width: 104,
			className: "productLibraryNoWrap",
			cell: (p) => (p.modified ? fmtDate(p.modified) : ""),
		},
		{
			key: "actions",
			sortable: false,
			width: canEdit ? 108 : 44,
			padding: "none",
			className: "productTableActions",
			cell: (p) => (
				<>
					<Tooltip title="Product Usage" arrow placement="top">
						<IconButton size="small" onClick={() => onUsage(p)}>
							<QueryStatsIcon fontSize="small" />
						</IconButton>
					</Tooltip>
					{canEdit && (
						<>
							<Tooltip title="Edit" arrow placement="top">
								<IconButton
									size="small"
									onClick={() => onEdit(p)}
								>
									<EditIcon fontSize="small" />
								</IconButton>
							</Tooltip>
							<Tooltip
								title={p.active ? "Retire" : "Reactivate"}
								arrow
								placement="top"
							>
								<IconButton
									size="small"
									color={p.active ? "error" : "primary"}
									onClick={() => onRetire(p)}
								>
									{p.active ? (
										<ArchiveOutlinedIcon fontSize="small" />
									) : (
										<UnarchiveOutlinedIcon fontSize="small" />
									)}
								</IconButton>
							</Tooltip>
						</>
					)}
				</>
			),
		},
	];
	const columns = allColumns.filter(
		(col) => !col.minWidth || screenWidth >= col.minWidth,
	);

	return (
		<div className="productTableView">
			<div className="bulkTagFilters">
				<TextField
					size="small"
					label="Search"
					placeholder="Name or description"
					InputLabelProps={{ shrink: true }}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					sx={{ width: 220 }}
				/>
				<FilterSelect
					label="Phase"
					value={phase}
					onChange={setPhase}
					width={170}
				>
					<MenuItem value={ANY}>All Phases</MenuItem>
					{(options?.phases || []).map((p) => (
						<MenuItem key={p.value} value={p.value}>
							{p.label}
						</MenuItem>
					))}
					<MenuItem value={NONE}>No Phase</MenuItem>
				</FilterSelect>
				<FilterSelect
					label="Service"
					value={service}
					onChange={setService}
					width={170}
				>
					<MenuItem value={ANY}>All Services</MenuItem>
					{(options?.services || []).map((s) => (
						<MenuItem key={s} value={s}>
							{s}
						</MenuItem>
					))}
					<MenuItem value={NONE}>No Service</MenuItem>
				</FilterSelect>
				<FilterSelect
					label="Component"
					value={component}
					onChange={setComponent}
					width={170}
				>
					<MenuItem value={ANY}>All Components</MenuItem>
					{(options?.components || []).map((c) => (
						<MenuItem key={c} value={c}>
							{c}
						</MenuItem>
					))}
					<MenuItem value={NONE}>No Component</MenuItem>
				</FilterSelect>
				<FilterSelect
					label="In Editor"
					value={library}
					onChange={setLibrary}
					width={170}
				>
					<MenuItem value={ANY}>All</MenuItem>
					{libraryOptions.map((o) => (
						<MenuItem key={o} value={o}>
							{o}
						</MenuItem>
					))}
				</FilterSelect>
				{filtersOn && (
					<button
						type="button"
						className="productLibraryTextButton"
						onClick={clearFilters}
					>
						Clear Filters
					</button>
				)}
				<div className="productLibrarySpacer" />
				<span className="productLibraryMuted">
					{filtersOn
						? `${sorted.length} of ${plural(products.length, "product")}`
						: plural(products.length, "product")}
				</span>
			</div>

			<div className="bulkTagTableContainer productTableContainer">
				<Table size="small" stickyHeader className="productTable">
					{/* Fixed widths sized to each column's content; Name gets
					    the rest, so the table fits without scrolling sideways */}
					<colgroup>
						{columns.map((col) => (
							<col
								key={col.key}
								style={
									col.width ? { width: col.width } : undefined
								}
							/>
						))}
					</colgroup>
					<TableHead>
						<TableRow>
							{columns.map((col) =>
								col.sortable === false ? (
									<TableCell key={col.key} />
								) : (
									<SortableHeaderCell
										key={col.key}
										sortKey={col.key}
										activeKey={sortKey}
										dir={sortDir}
										onSort={onSort}
										align={col.align}
									>
										{col.help ? (
											<HelpLabel
												label={col.label}
												help={col.help}
											/>
										) : (
											col.label
										)}
									</SortableHeaderCell>
								),
							)}
						</TableRow>
					</TableHead>
					<TableBody>
						{pageRows.map((p) => (
							<TableRow key={p.id} hover>
								{columns.map((col) => (
									<TableCell
										key={col.key}
										align={col.align}
										className={col.className}
										padding={col.padding}
									>
										{col.cell(p)}
									</TableCell>
								))}
							</TableRow>
						))}
					</TableBody>
				</Table>
				{!sorted.length && (
					<div className="productLibraryEmpty">
						{products.length ? (
							<>
								No products match these filters.{" "}
								<button
									type="button"
									className="productLibraryTextButton"
									onClick={clearFilters}
								>
									Clear Filters
								</button>
							</>
						) : (
							emptyText
						)}
					</div>
				)}
			</div>
			<TanStackTablePagination table={pager} pagination={pagination} />
		</div>
	);
};

export default ProductTable;
